/**
 * @file Integrates velocity into position.
 *
 * The single place where "how a body accelerates" is defined. Controllers only
 * write `Velocity.desired`; bodies that must fly straight (projectiles) opt out
 * with `steered = false` and are integrated verbatim.
 */

import { System } from '../ecs/System.js';
import { Transform, Velocity } from '../components/index.js';

export class MovementSystem extends System {
  static priority = 40;

  /**
   * @param {number} dt Delta time in seconds.
   * @returns {void}
   */
  update(dt) {
    const entities = this.world.query(Transform, Velocity);

    for (const entity of entities) {
      const transform = entity.get(Transform);
      const velocity = entity.get(Velocity);

      // Swept collision and render interpolation both need where we came from.
      transform.previousPosition.copy(transform.position);

      if (velocity.steered) this._steer(velocity, dt);

      transform.position.x += (velocity.linear.x + velocity.knockback.x) * dt;
      transform.position.y += velocity.linear.y * dt;
      transform.position.z += (velocity.linear.z + velocity.knockback.z) * dt;

      if (velocity.knockback.x !== 0 || velocity.knockback.z !== 0) {
        this._bleedKnockback(velocity, dt);
      }
    }
  }

  /**
   * Drains a shove back to zero at a fixed rate, so a hit reads as a shove and
   * not as a permanent change of course. Linear rather than exponential: the
   * push then has a knowable end, which is what makes it tunable.
   *
   * @param {import('../components/Velocity.js').Velocity} velocity Velocity component.
   * @param {number} dt Delta time in seconds.
   * @returns {void}
   * @private
   */
  _bleedKnockback(velocity, dt) {
    const { knockback } = velocity;
    const speed = Math.hypot(knockback.x, knockback.z);
    const drop = this.ctx.config.knockback.decay * dt;

    if (speed <= drop) {
      knockback.set(0, 0, 0);
      return;
    }

    const scale = (speed - drop) / speed;
    knockback.x *= scale;
    knockback.z *= scale;
  }

  /**
   * Moves `linear` toward `desired` at a rate that differs between speeding up
   * and slowing down, which is what gives the movement its weight.
   *
   * @param {import('../components/Velocity.js').Velocity} velocity Velocity component.
   * @param {number} dt Delta time in seconds.
   * @returns {void}
   * @private
   */
  _steer(velocity, dt) {
    const { linear, desired } = velocity;
    const braking = desired.lengthSq() < 1e-6;
    const rate = (braking ? velocity.deceleration : velocity.acceleration) * dt;

    const dx = desired.x - linear.x;
    const dz = desired.z - linear.z;
    const distance = Math.hypot(dx, dz);

    if (distance <= rate || distance < 1e-6) {
      linear.x = desired.x;
      linear.z = desired.z;
      return;
    }

    const scale = rate / distance;
    linear.x += dx * scale;
    linear.z += dz * scale;

    if (velocity.maxSpeed > 0) {
      const speed = Math.hypot(linear.x, linear.z);
      if (speed > velocity.maxSpeed) {
        const clampScale = velocity.maxSpeed / speed;
        linear.x *= clampScale;
        linear.z *= clampScale;
      }
    }
  }
}
